// segmentMerge — pure helpers that turn a room's stroke log segment set
// into a single seq-ordered event list (絵ログ replay T1 —
// docs/_wip/growth-timelapse-share-2026-07-02.md §5.1 / §5.3.1).
//
// The T0 consumer rules this module implements verbatim
// (`@oekaki/protocol` strokeLog.ts module header / §5.3.1):
//
//   - **Range is inclusive**: a segment covers `fromSeq..toSeq` inclusive.
//     Events inside are sparse (chat / member broadcasts consume seqs but
//     are never logged), so "no event at seq n" is normal.
//   - **Gaps are tolerated**: with no data loss consecutive segments
//     satisfy `next.fromSeq === prev.toSeq + 1`. A hole means events were
//     lost (flush PUT failure / crash) — we record the hole for
//     diagnostics and replay straight across it (drawing-core no-ops
//     orphan stroke.segment / stroke.end whose start is missing).
//   - **Overlap resolution**: after a WS server restart the seq counter
//     resumes from the last snapshot seq, so a pre-restart final flush can
//     share seq numbers with post-restart segments. For the shared RANGE
//     the segment with the **larger `flushedAtMs` wins** — the
//     post-restart events are the canonical timeline, and a seq the
//     winner covers but has no event at is genuinely empty (consumed by a
//     non-drawing broadcast post-restart), NOT a slot the loser may fill.
//     Resolution is therefore range-based, not per-event.
//
// Everything here is pure (no fetch / no engine) so the tricky corner
// cases are unit-testable in isolation; `strokeLogTimeline.ts` wires
// these helpers to the actual download pipeline.

import type { StrokeLogEvent, StrokeLogSegment } from '@oekaki/protocol';

/** Inclusive seq interval (mirrors the segment's `fromSeq..toSeq`). */
export interface SeqRange {
  readonly fromSeq: number;
  readonly toSeq: number;
}

/**
 * One entry of the dispatcher's `GET /rooms/:roomId/stroke-log` listing
 * (mirror of the server-side `StrokeLogSegmentRef` — the server package
 * exposes no type entry to web, same local-mirror policy as
 * `DispatcherClient`'s response shapes).
 */
export interface StrokeLogSegmentListEntry extends SeqRange {
  readonly url: string;
}

/**
 * Filter + order the listing for download. Segments that do not intersect
 * the requested trim range are never downloaded at all (= the §5.2 trim
 * also saves bandwidth / battery, not just replay time). Without a range
 * this is just a defensive `fromSeq` sort (the dispatcher already sorts,
 * but the merge logic below must not depend on transport ordering).
 */
export const planSegmentDownloads = (
  entries: readonly StrokeLogSegmentListEntry[],
  trim?: SeqRange,
): StrokeLogSegmentListEntry[] => {
  const kept =
    trim === undefined
      ? [...entries]
      : entries.filter((e) => e.toSeq >= trim.fromSeq && e.fromSeq <= trim.toSeq);
  kept.sort((a, b) => (a.fromSeq !== b.fromSeq ? a.fromSeq - b.fromSeq : a.toSeq - b.toSeq));
  return kept;
};

/** Result of `mergeSegments`. */
export interface MergedStrokeLog {
  /**
   * The canonical event list, ascending `seq`, with overlap ranges
   * resolved (larger `flushedAtMs` wins). This is what the replay driver
   * consumes.
   */
  readonly events: readonly StrokeLogEvent[];
  /**
   * Union of the surviving segment ranges, merged + sorted ascending.
   * The UI trim slider derives its bounds from this.
   */
  readonly coverage: readonly SeqRange[];
  /**
   * Holes between consecutive `coverage` intervals (= lost events, PUT
   * failure / crash). Diagnostic only — replay continues across them.
   */
  readonly gaps: readonly SeqRange[];
}

/** 昇順・互いに素な区間列に `seq` を含む区間があるか (二分探索)。 */
const isClaimed = (ranges: readonly SeqRange[], seq: number): boolean => {
  let lo = 0;
  let hi = ranges.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const r = ranges[mid] as SeqRange;
    if (seq < r.fromSeq) hi = mid - 1;
    else if (seq > r.toSeq) lo = mid + 1;
    else return true;
  }
  return false;
};

/** 昇順・互いに素な区間列へ `[from, to]` を足す (重なる / 隣り合う区間は 1 本に畳む)。 */
const claimRange = (ranges: SeqRange[], from: number, to: number): void => {
  // from-1 以上の toSeq を持つ最初の区間 (= 重なるか隣り合う可能性のある先頭)
  let lo = 0;
  let hi = ranges.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((ranges[mid] as SeqRange).toSeq < from - 1) lo = mid + 1;
    else hi = mid;
  }
  let end = lo;
  let nf = from;
  let nt = to;
  while (end < ranges.length && (ranges[end] as SeqRange).fromSeq <= to + 1) {
    const r = ranges[end] as SeqRange;
    if (r.fromSeq < nf) nf = r.fromSeq;
    if (r.toSeq > nt) nt = r.toSeq;
    end++;
  }
  ranges.splice(lo, end - lo, { fromSeq: nf, toSeq: nt });
};

/**
 * Merge downloaded segments into one seq-ordered event list.
 *
 * Overlap resolution is **range-based** (see module header): segments are
 * visited in priority order (`flushedAtMs` desc; ties broken by `toSeq`
 * desc then `fromSeq` desc for determinism), each claiming its full
 * `fromSeq..toSeq` range. A later-visited (= losing) segment only
 * contributes events whose seq falls outside every already-claimed range.
 *
 * Defensive extras (writer invariants we do not trust blindly):
 *   - events outside their own segment's declared range are dropped,
 *   - a malformed `fromSeq > toSeq` segment is ignored wholesale.
 */
export const mergeSegments = (segments: readonly StrokeLogSegment[]): MergedStrokeLog => {
  const valid = segments.filter((s) => s.fromSeq <= s.toSeq);
  const byPriority = [...valid].sort((a, b) => {
    if (a.flushedAtMs !== b.flushedAtMs) return b.flushedAtMs - a.flushedAtMs;
    if (a.toSeq !== b.toSeq) return b.toSeq - a.toSeq;
    return b.fromSeq - a.fromSeq;
  });

  // 確定済み範囲は「ソート済み・互いに素」の和集合で持つ (隣接も 1 本に畳む)。イベントごとの
  // 包含判定を二分探索にして、本数が増えても二乗にならないようにする (整数区間なので
  // 隣接を畳んでも包含の結果は変わらない)。
  const claimed: SeqRange[] = [];
  const events: StrokeLogEvent[] = [];
  for (const seg of byPriority) {
    for (const ev of seg.events) {
      if (ev.seq < seg.fromSeq || ev.seq > seg.toSeq) continue; // outside own range
      if (isClaimed(claimed, ev.seq)) continue; // lost overlap
      events.push(ev);
    }
    claimRange(claimed, seg.fromSeq, seg.toSeq);
  }
  events.sort((a, b) => a.seq - b.seq);

  const coverage = claimed;
  const gaps: SeqRange[] = [];
  for (let i = 1; i < coverage.length; i++) {
    const prev = coverage[i - 1];
    const next = coverage[i];
    if (prev === undefined || next === undefined) continue;
    // 和集合は隣接を畳んでいるので、この隙間は本物の穴。
    gaps.push({ fromSeq: prev.toSeq + 1, toSeq: next.fromSeq - 1 });
  }
  return { events, coverage, gaps };
};

/**
 * Trim an already-merged event list to an inclusive seq range (§5.2 時間
 * 範囲トリム). Mid-stroke cuts are fine: drawing-core no-ops a
 * `stroke.segment` / `stroke.end` whose `stroke.start` was trimmed away,
 * and a stroke whose `end` was trimmed still shows via the in-flight
 * buffer fold in `compositeAllForExport`.
 */
export const trimEventsToRange = (
  events: readonly StrokeLogEvent[],
  range: SeqRange | undefined,
): readonly StrokeLogEvent[] => {
  if (range === undefined) return events;
  return events.filter((ev) => ev.seq >= range.fromSeq && ev.seq <= range.toSeq);
};

/**
 * 順序付き離散範囲リスト (§4.7 系譜連結ドメイン、past-canvas-new-document
 * -2026-07-05.md) の不変条件を検証する: **非空・各範囲 fromSeq ≤ toSeq・昇順・
 * 互いに素** (隣接 = `prev.toSeq + 1 === next.fromSeq` は許容 — 互いに素で
 * ある)。違反は呼び出し側のバグなので `TypeError` を投げる (outcome union に
 * しない、repo の throw-for-bugs 流儀)。単一範囲は長さ 1 のリストとして通る。
 */
export const assertSeqRangeList = (ranges: readonly SeqRange[], context: string): void => {
  if (ranges.length === 0) {
    throw new TypeError(`${context}: seqRanges must be non-empty`);
  }
  let prev: SeqRange | undefined;
  for (const r of ranges) {
    // `!(a <= b)` 形で NaN も弾く。
    if (!(r.fromSeq <= r.toSeq)) {
      throw new TypeError(`${context}: invalid seq range ${r.fromSeq}..${r.toSeq}`);
    }
    if (prev !== undefined && !(r.fromSeq > prev.toSeq)) {
      throw new TypeError(
        `${context}: seqRanges must be ascending and disjoint (${prev.fromSeq}..${prev.toSeq} then ${r.fromSeq}..${r.toSeq})`,
      );
    }
    prev = r;
  }
};

/**
 * {@link trimEventsToRange} の離散範囲リスト版 (§4.7 可視フェーズ一般化):
 * いずれかの範囲に入るイベントだけを元の順序のまま残す。範囲間 (= 飛ばされた
 * 中間文書 / restore マーカー / 各文書の非復元 tail) のイベントは適用されない —
 * 系譜チェーンでは「範囲 k を再生し終えた状態 = 範囲 k+1 の文書の開始状態
 * (復元種) と bit-perfect 一致」が成立するため、ジャンプでのリセットや seed
 * 再構築は不要で、除外だけでよい (`lineageSeed.ts` ヘッダの「チェーンは重ねて
 * 適用する」意味論)。`ranges` は {@link assertSeqRangeList} 済み前提 (このもの
 * 自体は順序に依存しない包含判定)。undefined = 全範囲 (単一版と同じ)。
 */
export const trimEventsToRanges = (
  events: readonly StrokeLogEvent[],
  ranges: readonly SeqRange[] | undefined,
): readonly StrokeLogEvent[] => {
  if (ranges === undefined) return events;
  return events.filter((ev) => ranges.some((r) => ev.seq >= r.fromSeq && ev.seq <= r.toSeq));
};

/**
 * §5 土台 seed の適用範囲を求める (growth-timelapse-share-2026-07-02.md §5.2
 * トリム / §5 A 案「開始点より前を土台として無音 seed」)。
 *
 * トリム開始点 `visible.fromSeq` より前で **最後の全消し** (`whiteboard.clear`
 * かつ `layerId === undefined` = 全レイヤ wipe。判定基準は replay / cropBox /
 * `defaultTimelapseStartIndex` と統一) 以降を「現在の文書の、開始点までの描画」
 * = 土台とみなし、それを無音で seed するための inclusive seq 範囲
 * `[seedStart, visible.fromSeq - 1]` を返す:
 *
 *   - 開始点より前に全消しがある → `seedStart` = その最後の全消しの seq。全消し
 *     自体も seed に含めて適用する (= 白紙化 → 以降で土台を再蓄積)。開始点より前の
 *     別文書 (更に前の全消し以前) の描画は混ざらない。
 *   - 全消しが無い → `seedStart` = 文書先頭 (最初のイベントの seq) = 開始点より前
 *     すべてを土台にする (全消し無しの連続描画で中間開始したケース)。
 *   - 開始点より前にイベントが無い (= 文書先頭から開始) / 範囲未指定 → `null`
 *     (seed 不要)。
 *
 * **既定ケース不変の担保**: 編集画面の既定開始 (`defaultTimelapseStartIndex` =
 * 最後の全消しの次) では seed 範囲 = 「その全消し 1 件のみ」になり、白紙エンジンに
 * 白紙化を適用するだけ = 描画は一切増えない (drawing-core は透明レイヤを透明に
 * clear するだけ)。よって seed フェーズは視覚・バイト上の no-op となり、出力は
 * 従来と一致する。
 *
 * `events` は昇順 seq (merge 済み) を前提とする。
 */
export const computeSeedRange = (
  events: readonly StrokeLogEvent[],
  visible: SeqRange | undefined,
): SeqRange | null => {
  if (visible === undefined) return null;
  const fromSeq = visible.fromSeq;
  let seedStart: number | null = null;
  for (const ev of events) {
    if (ev.seq >= fromSeq) break; // ascending — no more events before the start
    if (seedStart === null) seedStart = ev.seq; // document head = first event before start
    if (ev.t === 'whiteboard.clear' && ev.layerId === undefined) {
      seedStart = ev.seq; // last full wipe at/before the start wins
    }
  }
  if (seedStart === null) return null; // trim start is the document head — no base
  return { fromSeq: seedStart, toSeq: fromSeq - 1 };
};
